import { Point, Size, point, rect, size } from '@geom';
import { expectDefined } from '@utils';
import Cytoscape from 'cytoscape';
import Cola from 'cytoscape-cola';
import FCose from 'cytoscape-fcose';
import LayoutUtilities from 'cytoscape-layout-utilities';
import { LayoutConstants } from '../layout/LayoutConstants';
import type { Doc } from './Doc';
import type { GroupLayout } from './GroupLayout';

export function makeCy(element?: HTMLElement): Cytoscape.Core {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  LayoutUtilities(Cytoscape);
  Cytoscape.use(FCose);
  Cytoscape.use(Cola as Cytoscape.Ext);

  return Cytoscape({
    container: element,
    styleEnabled: true,
    headless: true,
    // boxSelectionEnabled: false,
    // autounselectify: true,
    // minZoom: minZoom,
    // maxZoom: maxZoom,
    style: [
      {
        selector: 'node',
        style: {
          shape: 'rectangle'
          // opacity: 0.01,
          // "background-color": "#18e018",
          // "visibility": "hidden",
          // label: "data(id)",
          // label: "A\nB",
          // "font-size": 50,
          // "text-margin-y": 20,
          // color: "#ffffff",
        }
      },
      {
        selector: 'edge',
        style: {
          // opacity: 0,
          // width: 3,
          // "line-color": "#a2efa2",
          // "curve-style": "bezier",
          // // opacity: 0.5,
          // // color: "#ffffff",
          // // label: "data(id)",
          // // Add arrowhead
          // "target-arrow-color": "#a2efa2",
          // "target-arrow-shape": "triangle",
          // "source-endpoint": "outside-to-node",
          // "target-endpoint": "outside-to-node",
        }
      }
    ]
  });
}

export function cyNodeBounds(cyNode: Cytoscape.NodeSingular): {
  center: Point;
  size: Size;
} {
  const center = point(cyNode.position());
  const sizeBeforePadding = size(cyNode.width(), cyNode.height());
  const padding = parseFloat((cyNode.style('padding') as string).replace('px', ''));
  const s = sizeBeforePadding.add(size(padding * 2));
  return { center, size: s };
}

export function setCyNodeBounds(
  cyNode: Cytoscape.NodeSingular,
  bounds: { center: Point; size: Size }
) {
  cyNode.position(bounds.center.object);
  cyNode.style({
    width: Math.max(bounds.size.width, 0),
    height: Math.max(bounds.size.height, 0)
  });
}

export function docToCytoscape(doc: Doc, cy: Cytoscape.Core) {
  cy.batch(function () {
    cy.remove(cy.elements());

    for (const node of doc.graph.nodes) {
      const cyNode = cy.add({
        group: 'nodes',
        data: { id: node }
      });

      const layout = doc.nodeLayout(node);
      if (!doc.isGroup(node)) {
        setCyNodeBounds(cyNode, layout.bounds);
      } else {
        // Just use the center of the group for positional stability.
        const groupLayout = layout as GroupLayout;
        const mt = expectDefined(groupLayout.measuredTitle);
        const minWidth = mt.size.width + 2 * LayoutConstants.GROUP_TITLE_PADDING;
        setCyNodeBounds(
          cyNode,
          rect({ center: layout.bounds.center, size: size(minWidth, 0) })
        );
        cyNode.style({
          padding: LayoutConstants.GROUP_CONTENT_PADDING,
          // Make sure the group is no shorter than the title.
          minWidth,
          'top-extra-padding': mt.size.height + 2 * LayoutConstants.GROUP_TITLE_PADDING
        });
      }
    }

    for (const node of doc.graph.nodes) {
      const parent = doc.graph.parent(node);
      if (parent) {
        const cyNode = cy.getElementById(node);
        cyNode.move({ parent });
      }
    }

    for (const edge of doc.graph.edges) {
      cy.add({
        group: 'edges',
        data: {
          id: edge,
          source: doc.graph.source(edge),
          target: doc.graph.target(edge)
        }
      });
    }
  });
}

export function cytoscapeToDoc(cy: Cytoscape.Core, doc: Doc) {
  cy.nodes().forEach((node: Cytoscape.NodeSingular) => {
    const { center, size } = cyNodeBounds(node);
    doc.setNodeBounds(node.id(), { center, size });
  });
}
